/**
 * Solar 2D architecture diagram: main DC → Inverter → AC → Transformer flow
 * plus Other Systems (battery / UPS) and PV accessories under DC modules.
 */

import React, { useMemo } from 'react';
import type { VisualEquipment } from '../types';
import { getAssetTypeConfig, getEquipmentTypeDisplayLabel } from '../assetTypeConfig';
import { classifyAssetToLayer, type SolarLayer } from '../solarArchitectureRules';

const LAYER_STYLES: Record<
  Exclude<SolarLayer, 'accessory' | 'other'>,
  { title: string; headerBg: string; border: string; accent: string }
> = {
  dc: {
    title: 'DC Side',
    headerBg: '#0369A1',
    border: '#0369A1',
    accent: '#0369A1'
  },
  inverter: {
    title: 'Inverter Layer',
    headerBg: '#EF4444',
    border: '#EF4444',
    accent: '#EF4444'
  },
  ac: {
    title: 'AC Side',
    headerBg: '#EC4899',
    border: '#EC4899',
    accent: '#EC4899'
  },
  transmission: {
    title: 'Transmission',
    headerBg: '#F59E0B',
    border: '#F59E0B',
    accent: '#F59E0B'
  }
};

export interface ArchitectureView2DProps {
  equipment: VisualEquipment[];
  zoomLevel: number;
  availableAssets: Array<{ masterAssetId: string; name: string; description?: string }>;
}

function groupByLayer(equipment: VisualEquipment[]) {
  const dc: VisualEquipment[] = [];
  const dcAccessories: VisualEquipment[] = [];
  const inverter: VisualEquipment[] = [];
  const ac: VisualEquipment[] = [];
  const transmission: VisualEquipment[] = [];
  const other: VisualEquipment[] = [];

  for (const eq of equipment) {
    if (eq.isGhost) continue;
    if (eq.isVirtual) continue;
    const layer = classifyAssetToLayer(eq.assetType);
    switch (layer) {
      case 'accessory':
        dcAccessories.push(eq);
        break;
      case 'other':
        other.push(eq);
        break;
      case 'dc':
        dc.push(eq);
        break;
      case 'inverter':
        inverter.push(eq);
        break;
      case 'ac':
        ac.push(eq);
        break;
      case 'transmission':
        transmission.push(eq);
        break;
      default:
        other.push(eq);
    }
  }

  return { dc, dcAccessories, inverter, ac, transmission, other };
}

function EquipmentChip({
  eq,
  availableAssets
}: {
  eq: VisualEquipment;
  availableAssets: ArchitectureView2DProps['availableAssets'];
}) {
  const config = getAssetTypeConfig(eq.assetType, availableAssets, {
    masterAssetId: eq.masterAssetId,
    catalogName: eq.projectAssetName
  });
  const Icon = config.icon;
  const typeLabel = getEquipmentTypeDisplayLabel(eq.assetType, eq.projectAssetName);
  return (
    <div
      className="group flex flex-col gap-1 rounded-xl border bg-white/95 px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md"
      style={{
        borderColor: `${config.color}40`,
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderLeftColor: config.color,
        boxShadow: `0 1px 2px rgba(0,0,0,0.04), inset 0 0 0 1px ${config.color}14`
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-white/80 shadow-inner"
          style={{ background: `linear-gradient(145deg, ${config.color}22, ${config.color}0d)` }}
        >
          <Icon size={18} style={{ color: config.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[12px] font-bold leading-snug tracking-tight"
            style={{ color: config.color }}
            title={eq.name}
          >
            {eq.name}
          </p>
          <p
            className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: config.color, opacity: 0.72 }}
            title={typeLabel}
          >
            {typeLabel}
          </p>
        </div>
      </div>
    </div>
  );
}

function LayerBlock({
  style,
  emptyHint,
  children,
  footer
}: {
  style: (typeof LAYER_STYLES)['dc'];
  emptyHint: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const childList = React.Children.toArray(children).filter(Boolean);
  const hasMain = childList.length > 0;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 bg-white/95 shadow-md backdrop-blur-sm"
      style={{ borderColor: style.border + '55' }}
    >
      <div
        className="relative px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm"
        style={{ background: style.headerBg }}
      >
        <span className="relative z-10">{style.title}</span>
      </div>
      <div className="min-h-[80px] space-y-2.5 p-3.5">
        {hasMain ? (
          <div className="space-y-2.5">{children}</div>
        ) : (
          <div className="flex h-[64px] items-center justify-center rounded-xl border border-dashed border-slate-300/90 bg-slate-50/90 text-center text-[10px] text-slate-400">
            {emptyHint}
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}

function FlowArrow({ accent }: { accent: string }) {
  return (
    <div className="flex flex-col items-center py-0.5">
      <div className="h-2 w-px rounded-full" style={{ background: `${accent}55` }} />
      <svg width="28" height="22" viewBox="0 0 28 22" style={{ color: accent }}>
        <path
          d="M14 2 L14 14 M8 10 L14 14 L20 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="h-2 w-px rounded-full" style={{ background: `${accent}55` }} />
    </div>
  );
}

export default function ArchitectureView2D({
  equipment,
  zoomLevel,
  availableAssets
}: ArchitectureView2DProps) {
  const grouped = useMemo(() => groupByLayer(equipment), [equipment]);

  const dcAccessoriesFooter =
    grouped.dcAccessories.length > 0 ? (
      <div className="mt-2 rounded-xl border border-sky-200/90 bg-gradient-to-br from-sky-50/95 to-white p-2.5 shadow-sm">
        <p className="mb-2 text-[9px] font-bold uppercase tracking-wide text-sky-900/90">
          PV Module Accessories
        </p>
        <div className="space-y-2">
          {grouped.dcAccessories.map(eq => (
            <EquipmentChip key={eq.id} eq={eq} availableAssets={availableAssets} />
          ))}
        </div>
      </div>
    ) : (
      <div className="mt-2 rounded-xl border border-dashed border-sky-200/80 bg-sky-50/50 px-2 py-2 text-center text-[9px] font-medium text-sky-700/80">
        PV Module Accessories (grouped with modules)
      </div>
    );

  const mainFlowLayers: Array<{
    key: keyof typeof LAYER_STYLES;
    items: VisualEquipment[];
    emptyHint: string;
    footer?: React.ReactNode;
  }> = [
    {
      key: 'dc',
      items: grouped.dc,
      emptyHint: 'PV modules, strings, DC cable…',
      footer: dcAccessoriesFooter
    },
    {
      key: 'inverter',
      items: grouped.inverter,
      emptyHint: 'Inverter / String Inverter'
    },
    {
      key: 'ac',
      items: grouped.ac,
      emptyHint: 'AC Cable, LT Panel (logical)'
    },
    {
      key: 'transmission',
      items: grouped.transmission,
      emptyHint: 'Power Transformer (optional)'
    }
  ];

  return (
    <div
      className="flex justify-center bg-gradient-to-b from-slate-50/80 via-white to-slate-50/80 p-4 sm:p-6"
      style={{
        transform: `scale(${zoomLevel})`,
        transformOrigin: 'top center'
      }}
    >
      <div className="flex w-full max-w-6xl flex-col gap-0 md:flex-row md:items-start md:gap-8">
        {/* Main flow column */}
        <div className="relative mx-auto w-full max-w-md flex-1">
          <div
            className="pointer-events-none absolute left-[10px] top-0 hidden h-full w-[3px] rounded-full md:block"
            style={{
              background: `linear-gradient(180deg, ${LAYER_STYLES.dc.accent} 0%, ${LAYER_STYLES.inverter.accent} 32%, ${LAYER_STYLES.ac.accent} 66%, ${LAYER_STYLES.transmission.accent} 100%)`,
              opacity: 0.35
            }}
          />
          <div className="relative space-y-0 md:pl-5">
            {mainFlowLayers.map((layer, idx) => (
              <React.Fragment key={layer.key}>
                {idx > 0 && <FlowArrow accent={LAYER_STYLES[layer.key].accent} />}
                <LayerBlock
                  style={LAYER_STYLES[layer.key]}
                  emptyHint={layer.emptyHint}
                  footer={layer.footer}
                >
                  {layer.items.map(eq => (
                    <EquipmentChip key={eq.id} eq={eq} availableAssets={availableAssets} />
                  ))}
                </LayerBlock>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Other systems — right box */}
        <div
          className="mx-auto mt-6 w-full max-w-sm flex-shrink-0 overflow-hidden rounded-2xl border-2 border-dashed border-slate-300/90 bg-gradient-to-br from-slate-50/95 to-white shadow-md md:mt-0 md:min-h-[220px]"
          style={{ borderColor: '#6B728080' }}
        >
          <div
            className="px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white"
            style={{ background: 'linear-gradient(90deg, #6B7280 0%, #4B5563 100%)' }}
          >
            Other Systems
          </div>
          <div className="space-y-2.5 p-3.5">
            {grouped.other.length > 0 ? (
              grouped.other.map(eq => (
                <EquipmentChip key={eq.id} eq={eq} availableAssets={availableAssets} />
              ))
            ) : (
              <div className="flex min-h-[96px] items-center justify-center rounded-xl border border-dashed border-slate-300/90 bg-white/70 text-center text-[10px] font-medium text-slate-400">
                Battery cells, UPS (separate from main flow)
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
